import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { articles, categories } from '@/mocks/anime';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-200/70 text-foreground-300 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </span>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查看的文章不存在或已被删除</p>
            <Link
              to="/"
              onClick={scrollToTop}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleCategory = categories.find((c) => c.slug === article.category);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},手机动漫,风车动漫,动漫资讯`}
        canonicalUrl={`https://fengchedongman.com/article/${article.id}`}
        ogType="article"
        breadcrumbs={[
          { name: '首页', url: 'https://fengchedongman.com/' },
          { name: `${article.categoryName}动漫`, url: `https://fengchedongman.com/category/${article.category}` },
          { name: article.title, url: `https://fengchedongman.com/article/${article.id}` },
        ]}
        articleData={{
          publishedTime: article.date,
          modifiedTime: article.date,
          author: article.author,
          tags: article.tags,
        }}
        type="article"
      />

      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 flex-wrap" aria-label="面包屑导航">
              <Link to="/" onClick={scrollToTop} className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.category}`} onClick={scrollToTop} className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">{article.categoryName}动漫</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Content */}
        <article className="py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {articleCategory && (
                  <Link
                    to={`/category/${article.category}`}
                    onClick={scrollToTop}
                    className="flex items-center gap-1 text-xs bg-primary-100 text-primary-700 px-3 py-1 rounded-full hover:bg-primary-200 transition-colors cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${articleCategory.icon} text-xs`}></i>
                    </span>
                    {article.categoryName}
                  </Link>
                )}
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.author}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded">
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            {/* Cover Image */}
            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8 bg-background-200">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            {/* Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: article.content
                  .replace(/<h4>/g, '<h4 class="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-3">')
                  .replace(/<p>/g, '<p class="text-sm md:text-base text-foreground-700 mb-4 leading-relaxed">'),
              }}
            />

            {/* Tags bottom */}
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full cursor-pointer hover:bg-secondary-200 transition-colors">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    onClick={scrollToTop}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-background-200">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        title={ra.title}
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">{ra.categoryName}</span>
                        <span className="text-xs text-foreground-400">{ra.date}</span>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}